/**
 * What opens over the page: the talk (the room's real player, its offer and chapters), a room's
 * sessions, the person's answers and what they are told about, their photo (taken with the camera,
 * or chosen), and Record a video (the shared recorder: up to 10:00, review, Retake, Send).
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import type { WatchCaption, WatchInteraction, WatchSession, WatchSessionReplay } from "../../../shared/types";
import { brandVars } from "../brandVars";
import { currentChapter, Player, type PlayerHandle, useOfferArrived } from "../TalkPlayer";
import { replayTalk } from "../series";
import { useArrivals } from "../useArrivals";
import { useWatchProgress } from "../watchProgress";
import { type SheetKey, usePage } from "./context";
import { IcBack, IcCamera, IcImage, IcPause, IcPerson, IcPlay, IcX } from "./icons";
import { clock, talkLength } from "./model";
import { kindWord, timeOf } from "./talk";
import { ContinueCard, continueWords, OfferLink, PinList } from "./Pinned";
import { AnswersPanel } from "./Answers";
import { RoomRow, SeriesBlock } from "./Sessions";
import { longDay, NextLive, Switch, TellMe } from "./Parts";
import { VideoRecorder } from "../../components/recorder/VideoRecorder";
import { NOTE_MAX_SEC } from "../../lib/noteLimits";
import { pageNoteApi } from "../../lib/noteUpload";

const CLOSE_MS = 360;

// --- the talk ---

function TalkSheet(): ReactNode {
  const { data, token, playing, me, host } = usePage();
  const room = data.sessions.find((s) => s.webinar_id === playing?.webinar_id) ?? null;
  const [replay, setReplay] = useState<WatchSessionReplay | null>(null);
  const [trouble, setTrouble] = useState("");
  const sessionId = playing?.session_id;
  const latest = room?.live?.series?.sessions.filter((s) => s.state === "ended").at(-1)?.id;
  useEffect(() => {
    setReplay(null);
    setTrouble("");
    if (!sessionId || sessionId === latest) return;
    let alive = true;
    api.watch
      .sessionReplay(token, sessionId)
      .then((r) => alive && setReplay(r))
      .catch(() => alive && setTrouble("That replay did not load. Try again."));
    return () => {
      alive = false;
    };
  }, [token, sessionId, latest]);
  if (!room) return null;
  if (trouble) return <p className="sh-meta">{trouble}</p>;
  if (sessionId && sessionId !== latest && !replay) return <p className="sh-meta">Loading the replay</p>;
  return (
    <Talk
      room={{ ...(replay ? replayTalk(room, replay) : room), ...(playing?.at !== undefined ? { watched_sec: playing.at } : {}) }}
      sessionId={sessionId}
      start={Boolean(playing?.start)}
      viewerEmail={me.email}
      hostFirst={host.first}
    />
  );
}

/** The talk's words, asked for once the sheet opens: a recorded talk's, or the replay's own for a series' session. */
function useTalkWords(token: string, webinarId: string, sessionId?: string): WatchCaption[] {
  const [words, setWords] = useState<WatchCaption[]>([]);
  useEffect(() => {
    let alive = true;
    setWords([]);
    api.watch
      .talk(token, webinarId, sessionId)
      .then((r) => alive && setWords(r.captions))
      .catch(() => {
        /* the talk plays without its words */
      });
    return () => {
      alive = false;
    };
  }, [token, webinarId, sessionId]);
  return words;
}

function Talk({
  room,
  sessionId,
  start,
  viewerEmail,
  hostFirst,
}: {
  room: WatchSession;
  sessionId?: string;
  start: boolean;
  viewerEmail: string;
  hostFirst: string;
}): ReactNode {
  const { token, data, refresh, guest } = usePage();
  const player = useRef<PlayerHandle | null>(null);
  const words = useTalkWords(token, room.webinar_id, sessionId);
  const [playing, setPlaying] = useState(false);
  // Why: the place they reached shows on the Continue card and the timeline once the talk closes.
  useEffect(() => () => refresh(), [refresh]);
  const [mine, setMine] = useState<Record<string, WatchInteraction["my_response"]>>({});
  const [at, setAt] = useState(room.watched_sec);
  const talk = useMemo(
    () => ({ ...room, watched_sec: at, interactions: room.interactions.map((i) => (i.id in mine ? { ...i, my_response: mine[i.id] } : i)) }),
    [room, at, mine],
  );
  const setAnswer = useCallback((id: string, value: WatchInteraction["my_response"]) => setMine((m) => ({ ...m, [id]: value })), []);
  const { arrivals, answer } = useArrivals({ token, session: talk, active: true, setMine: setAnswer });
  const report = useWatchProgress(token, room.webinar_id, (sec) => setAt(sec), sessionId);
  const video = () => document.querySelector<HTMLVideoElement>(".tp .tp-talk video");
  const play = (sec: number) => {
    player.current?.jumpTo(sec);
    video()
      ?.play()
      .catch(() => {});
  };
  const now = currentChapter(room.chapters, at);
  const offer = room.offer;
  // As in the room: the offer lands at the second the host set, and stays once it is up.
  const arrived = useOfferArrived(offer, at);
  return (
    <>
      <div className="fw-watch tp-talk" style={brandVars(data.brand)}>
        <Player
          key={sessionId}
          session={talk}
          onProgress={report}
          arrivals={arrivals}
          onAnswer={answer}
          chrome="sheet"
          captions={words}
          autoPlay={start}
          onPlayState={setPlaying}
          player={player}
          viewerEmail={viewerEmail}
          onConvert={() => api.watch.signup(token, { webinar_id: room.webinar_id }).catch(() => {})}
          onOfferVideo={(state) => api.watch.offerVideo(token, { webinar_id: room.webinar_id, state }).catch(() => {})}
        />
      </div>
      <h2 className="ws-title" dir="auto">{room.title}</h2>
      <p className="sh-meta" style={{ marginTop: 4 }}>
        {room.live ? "Replay" : kindWord(guest.rooms.find((r) => r.webinarId === room.webinar_id)?.kind)} · {talkLength(room.duration_sec)} · with {room.host_name}
      </p>
      <div className="sh-acts">
        {playing ? (
          <button type="button" className="btn btn-p" onClick={() => player.current?.pause()}>
            <IcPause s={16} />
            Pause
          </button>
        ) : (
          <button type="button" className="btn btn-p" onClick={() => play(at >= room.duration_sec - 5 ? 0 : at)}>
            <IcPlay s={16} />
            {continueWords({ ...room, watched_sec: at })}
          </button>
        )}
        {at > 0 ? (
          <button type="button" className="btn btn-g" onClick={() => play(0)}>
            Start over
          </button>
        ) : null}
      </div>
      {offer && arrived ? (
        <div className="ws-offer">
          <div className="wo-x">
            <span className="pe"><bdi>{hostFirst}</bdi>’s offer from this talk</span>
            {offer.text ? <h4>{offer.text}</h4> : null}
          </div>
          <OfferLink room={{ ...talk, watched_sec: Math.max(talk.watched_sec, offer.appears_at ?? 0) }} className="btn btn-p" />
        </div>
      ) : null}
      {room.chapters.length ? (
        <>
          <h3 className="sh-sec">Chapters</h3>
          <ol className="chapters">
            {room.chapters.map((c, i) => (
              <li key={`${c.t}-${i}`} className={i < now ? "done" : ""}>
                <button type="button" onClick={() => play(c.t)}>
                  <span className="ct">{clock(c.t)}</span>
                  <span className="cn" dir="auto">{c.title}</span>
                  {i === now && at > 0 ? <span className="here">You’re here</span> : c.sub ? <span className="tag" dir="auto">{c.sub}</span> : null}
                </button>
              </li>
            ))}
          </ol>
        </>
      ) : null}
    </>
  );
}

// --- a room's sessions, the person's answers, what they are told about ---

function RoomSheet(): ReactNode {
  const { data, roomOpen } = usePage();
  const room = data.sessions.find((s) => s.webinar_id === roomOpen);
  if (!room) return null;
  return room.live?.series ? (
    <SeriesBlock room={room} />
  ) : (
    <ul className="slist">
      <RoomRow room={room} />
    </ul>
  );
}

function AnswersSheet(): ReactNode {
  return (
    <>
      <h2 className="ws-title">Your answers</h2>
      <AnswersPanel />
    </>
  );
}

function TellSheet(): ReactNode {
  return (
    <div className="jp-tell">
      <TellMe />
    </div>
  );
}

/** Every pin of the private thread, from the one line the iPad upright keeps under the head. */
function PinsSheet(): ReactNode {
  const { closeSheet, guest } = usePage();
  return (
    // Why the click closes: a pin row scrolls the thread to its line, which the open sheet would hide.
    <div className="jp-pinsheet" onClick={(e) => (e.target as HTMLElement).closest(".pr-go") && closeSheet()}>
      <h3>Pinned</h3>
      <ContinueCard />
      <NextLive next={guest.nextLive} />
      <PinList />
    </div>
  );
}

/** A circle's next call from the line under its name: the day, the time and length, and "I am coming". */
function CallSheet(): ReactNode {
  const { token, call } = usePage();
  const [coming, setComing] = useState<boolean | null>(null);
  if (!call) return null;
  const going = coming ?? call.next.coming ?? false;
  const rsvp = (on: boolean) => {
    setComing(on);
    api.guestTalk.rsvp(token, call.circleId, on).catch(() => setComing(null));
  };
  return (
    <div className="jp-callsheet">
      <small>Next call · {call.name}</small>
      <h3>{longDay(call.next.startsAt)}</h3>
      <p>
        {timeOf(call.next.startsAt)} · {call.next.lengthMin} min
      </p>
      <div className="jp-set flat">
        <span>
          <b>I am coming</b>
        </span>
        <Switch on={going} label="I am coming" onChange={rsvp} />
      </div>
    </div>
  );
}

// --- their photo ---

/** A phone's own camera opens from a picker; a laptop's opens here, the way Record a video opens it. */
const ownCamera = () => typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;

function PhotoSheet(): ReactNode {
  const { me, setPhoto, closeSheet, notYou, toast, openSheet } = usePage();
  const take = useRef<HTMLInputElement>(null);
  const choose = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const put = (file: File | null) => {
    setBusy(true);
    setPhoto(file)
      .then(closeSheet)
      .catch(() => toast("That picture did not go up. Try another."))
      .finally(() => setBusy(false));
  };
  const picked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) put(f);
  };
  return (
    <div className="photo-ask">
      <span className="ph">{me.avatar ? <img src={me.avatar} alt="" /> : <IcPerson s={40} />}</span>
      <h3>Your photo</h3>
      <div className="pa">
        <button type="button" className="btn btn-p" disabled={busy} onClick={() => (ownCamera() ? take.current?.click() : openSheet("snap", true))}>
          <IcCamera s={18} />
          Take one
        </button>
        <button type="button" className="btn btn-s" disabled={busy} onClick={() => choose.current?.click()}>
          <IcImage s={18} />
          Choose one
        </button>
      </div>
      {me.avatar ? (
        <button type="button" className="btn btn-q" style={{ marginTop: 6 }} disabled={busy} onClick={() => put(null)}>
          Remove
        </button>
      ) : (
        <button type="button" className="btn btn-q" style={{ marginTop: 6 }} onClick={closeSheet}>
          Not now
        </button>
      )}
      <div className="whome">
        <span className="wx">
          <b>{me.name}</b>
          <small>{me.email}</small>
        </span>
        <button type="button" className="btn btn-q" onClick={notYou}>
          Not you?
        </button>
      </div>
      <input ref={take} type="file" accept="image/*" capture="user" hidden onChange={picked} />
      <input ref={choose} type="file" accept="image/*" hidden onChange={picked} />
    </div>
  );
}

// --- their photo, from the camera ---

/** The camera, a press of the shutter, the prototype's flash, and the picture becomes their photo. */
function SnapSheet(): ReactNode {
  const { closeSheet, setPhoto, toast } = usePage();
  const live = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const [camOn, setCamOn] = useState(false);
  const [problem, setProblem] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(0);
  useEffect(() => {
    let alive = true;
    if (!navigator.mediaDevices?.getUserMedia) {
      setProblem("This browser can't open a camera. Choose a photo instead.");
      return;
    }
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((s) => {
        if (!alive) {
          for (const tr of s.getTracks()) tr.stop();
          return;
        }
        stream.current = s;
        if (live.current) live.current.srcObject = s;
      })
      .catch(() => alive && setProblem("We couldn't open your camera. Allow it for this page, then try again."));
    return () => {
      alive = false;
      for (const tr of stream.current?.getTracks() ?? []) tr.stop();
      stream.current = null;
    };
  }, []);
  const snap = () => {
    const v = live.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const g = c.getContext("2d");
    if (!g) return;
    // Why mirrored: the picture is the one they saw in the camera, as a phone's front camera keeps it.
    g.translate(c.width, 0);
    g.scale(-1, 1);
    g.drawImage(v, 0, 0, c.width, c.height);
    setFlash((n) => n + 1);
    setBusy(true);
    c.toBlob(
      (b) => {
        if (!b) {
          setBusy(false);
          return;
        }
        setPhoto(new File([b], "photo.jpg", { type: "image/jpeg" }))
          .then(closeSheet)
          .catch(() => toast("That picture did not go up. Try again."))
          .finally(() => setBusy(false));
      },
      "image/jpeg",
      0.9,
    );
  };
  return (
    <div className="vr" role="group" aria-label="Take a photo">
      <div className="vr-st ready">
        {/* Why the shutter waits for a frame: a press before the camera draws one would take nothing. */}
        <video ref={live} className="vr-cam" muted playsInline autoPlay aria-label="Your camera" onLoadedData={() => setCamOn(true)} />
        {flash ? <div key={flash} className="flash" /> : null}
        <button type="button" className="vr-x" aria-label="Close" onClick={closeSheet}>
          <IcX s={16} />
        </button>
      </div>
      <div className="vr-c">
        {problem ? (
          <p className="vr-msg">{problem}</p>
        ) : (
          <button type="button" className="vr-go shot" disabled={!camOn || busy} aria-label="Take the photo" onClick={snap}>
            <i />
          </button>
        )}
      </div>
    </div>
  );
}

// --- Record a video ---

function RecordSheet(): ReactNode {
  const { closeSheet, sendVideo, toast, token } = usePage();
  const upload = useMemo(() => pageNoteApi(token), [token]);
  return (
    <VideoRecorder
      limitSec={NOTE_MAX_SEC.video}
      onClose={closeSheet}
      upload={upload}
      done={{
        label: "Send",
        icon: "upload",
        onUse: (blob, _seconds, live) => {
          sendVideo(blob, live);
          closeSheet();
          toast("Sending your video.");
        },
      }}
    />
  );
}

// --- the layer ---

const BODY: Record<SheetKey, () => ReactNode> = { talk: TalkSheet, room: RoomSheet, answers: AnswersSheet, tell: TellSheet, pins: PinsSheet, call: CallSheet, photo: PhotoSheet, snap: SnapSheet, rec: RecordSheet };

export function Sheets(): ReactNode {
  const { sheets, closeSheet, backSheet, host } = usePage();
  const key = sheets.at(-1) ?? null;
  const [shown, setShown] = useState<SheetKey | null>(key);
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (key) {
      setShown(key);
      const raf = window.requestAnimationFrame(() => window.requestAnimationFrame(() => setOn(true)));
      return () => window.cancelAnimationFrame(raf);
    }
    setOn(false);
    const t = window.setTimeout(() => setShown(null), CLOSE_MS);
    return () => window.clearTimeout(t);
  }, [key]);
  useEffect(() => {
    if (!key) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !document.querySelector(".tp .viewer")) closeSheet();
    };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [key, closeSheet]);
  if (!shown) return null;
  const Body = BODY[shown];
  const stacked = sheets.length > 1;
  return (
    <div className={`layer${on && key ? " on" : ""}`}>
      <div className="scrim" onClick={closeSheet} />
      <div className={`sheet${shown === "photo" || shown === "call" ? " small" : ""}${shown === "rec" || shown === "snap" ? " recs" : ""}`} role="dialog" aria-modal="true" data-key={shown}>
        {shown === "rec" || shown === "snap" ? null : (
          <div className="sh-head">
            {stacked ? (
              <button type="button" className="sh-back" onClick={backSheet}>
                <IcBack s={20} />
                {host.first}
              </button>
            ) : null}
            <span style={{ flex: 1 }} />
            <button type="button" className="sh-x" aria-label="Close" onClick={closeSheet}>
              <IcX s={15} />
            </button>
          </div>
        )}
        <div className="sh-body">
          <Body />
        </div>
      </div>
    </div>
  );
}
